import GameSignals from '@/signals/GameSignals'
import { BaseChildState } from '@stateMachine/BaseChildState'
import Facade from '@/Facade'
import { getHoldAndWinFinalWinAmount, isEndingHoldAndWinRound } from '@/states/holdandwin/HoldAndWinFinalWin'
import { getFreeSpinsFinalWinAmount, isFreeSpinsFeatureCompleteRound } from '@/states/freespins/FreeSpinsFinalWin'
import { WinningUtil } from '@/managers/winning/WinningUtil'
import { capWinIncrement } from '@/states/round/MaxWinCap'

export class BigWinState extends BaseChildState {
    public canTrigger(): boolean {
        // FS feature complete (after H&W end popup rewalk, or plain last FS): total_fs_win_amount.
        // Allowed even when skipFinalWinPresentation is set after an in-FS Hold & Win rewalk.
        if (isFreeSpinsFeatureCompleteRound(this._model)) {
            const finalWinAmount = getFreeSpinsFinalWinAmount(this._model)
            this._model.bigWinLevel = WinningUtil.getBigWinLevel(finalWinAmount, this._model.currentBaseBet)
            return this._model.bigWinLevel !== null
        }

        if ((Facade as any).skipFinalWinPresentationAfterHoldAndWin) {
            return false
        }

        return this._model.bigWinLevel !== null
    }

    public begin() {
        GameSignals.showBigWinComplete.connect(this.end)
        // Connect skip immediately (same as other commendations) — do not wait for showBigWin.
        GameSignals.stopButtonClicked.connect(this.onSkipBigWin)
        GameSignals.spinButtonClicked.connect(this.onSkipBigWin)
        // Allow click-anywhere global skip (UIManager otherwise blocks on isPopupBlockingInput).
        ;(Facade as any).isBigWinPresentationActive = true

        // FS feature-complete must win over stale isEndingHoldAndWinRound (hold_and_win_extra
        // stays on spinData after H&W summary) — otherwise commendation uses win_amount.
        const isFreeSpinsSummary = isFreeSpinsFeatureCompleteRound(this._model)
        const isHoldAndWinSummary = !isFreeSpinsSummary && isEndingHoldAndWinRound(this._model)
        let winAmount: number = isFreeSpinsSummary
            ? getFreeSpinsFinalWinAmount(this._model)
            : isHoldAndWinSummary
              ? getHoldAndWinFinalWinAmount(this._model)
              : this._model.spinData.base_win_currency
        let bigWinLevel: number = this._model.bigWinLevel

        if (isHoldAndWinSummary || isFreeSpinsSummary) {
            // Full Big→Mega→Ultra→Ultra Mega ladder for feature-complete totals, which the
            // feature-win helpers have already clamped to the max win.
            bigWinLevel = WinningUtil.getBigWinLevel(winAmount, this._model.currentBaseBet)
            this._model.bigWinLevel = bigWinLevel
        } else {
            const cappedWinAmount = capWinIncrement(this._model, this._model.winCounter.totalWin, winAmount)

            if (cappedWinAmount !== winAmount) {
                winAmount = cappedWinAmount

                //## Get the bigwin level for the adjusted win amount, in case the original win amount was above the max win limit.
                //## A null level means the clamped amount is under the commendation minimum — no presentation.
                const clampedLevel = WinningUtil.getBigWinLevel(winAmount, this._model.currentBaseBet)
                bigWinLevel = clampedLevel === null ? null : Math.min(bigWinLevel, clampedLevel)
            }
        }

        if (bigWinLevel === null) {
            ;(Facade as any).isBigWinPresentationActive = false
            this.end()
            return
        }

        GameSignals.bigWinPresentation.emit({
            win: winAmount,
            bet: this._model.currentBaseBet,
            bigWinLevel,
            // Feature-complete (H&W / FS): full ladder. Mid-FS per-spin: highest tier only.
            highestOnly: !isHoldAndWinSummary && !isFreeSpinsSummary && Boolean(this._model.roundState?.isFreeSpins),
            // H&W: Win Meter updates on total-win popup, not during commendation count-up.
            // FS feature-complete: FS summary owns the final meter credit.
            skipWinCounter: isHoldAndWinSummary || isFreeSpinsSummary,
        })
    }

    private onSkipBigWin = () => {
        GameSignals.skipBigWin.emit()
    }

    public cleanUp() {
        ;(Facade as any).isBigWinPresentationActive = false
        GameSignals.stopButtonClicked.disconnect(this.onSkipBigWin)
        GameSignals.spinButtonClicked.disconnect(this.onSkipBigWin)
        GameSignals.showBigWinComplete.disconnect(this.end)
    }
}
